// Adapted from apps/admin/src/pages/dashboard/gps-tracking/components/vehicle-filters.tsx (PRD
// #875): one day instead of a period with times, and the govdash district / MVO filter by the
// search.
import React, { memo } from "react";
import { DatePicker } from "@racoon/ui";
import { Filter, Search } from "lucide-react";
import { useT, Namespace } from "@racoon/i18n";

interface VehicleFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  day: Date;
  onDayChange: (day: Date) => void;
  /** The day's routes are loading: the picker shows a spinner in place of its icon. */
  loading?: boolean;
  /** Districts and MVOs picked in the filter, for the badge on its button. */
  activeFilterCount: number;
  onOpenFilters: () => void;
}

const VehicleFiltersComponent: React.FC<VehicleFiltersProps> = ({
  searchQuery,
  onSearchChange,
  day,
  onDayChange,
  loading,
  activeFilterCount,
  onOpenFilters,
}) => {
  const t = useT();

  return (
    <div className="p-4 border-b space-y-3">
      <div className="flex items-center gap-2" data-testid="gps-tracking-period">
        {/* The prev / next day arrows walk a truck's days one by one, as on the monitor. */}
        <DatePicker
          value={day}
          onChange={(date) => date && onDayChange(date)}
          maxDate={new Date()}
          loading={loading}
          showDayNav
          navWrapperClassName="flex w-full items-center justify-between gap-1"
        />
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t(Namespace.Govdash, "gpsTracking.searchPlaceholder")}
            data-testid="gps-tracking-search"
            className="w-full text-sm px-3 h-8 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 " />
        </div>
        <button
          type="button"
          aria-label={t(Namespace.Govdash, "actions.openFilters")}
          data-testid="gps-tracking-filter"
          onClick={onOpenFilters}
          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md border hover:bg-slate-50 transition-colors"
        >
          <Filter className="w-4 h-4 fill-brand-blue" strokeWidth={0} />
          {activeFilterCount > 0 && (
            <span
              data-testid="gps-tracking-filter-count"
              className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-blue px-1 text-[10px] font-semibold leading-none text-white"
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export const VehicleFilters = memo(VehicleFiltersComponent);
